import {Injectable} from '@angular/core';
import {DataPoint} from '../../models/data-point';

import moment from 'moment-timezone';
import {ChartConfig} from '../../components/highcharts/timeseries-chart/interfaces/timeseries-chart-config.interface';
import {TelemetryData} from '../../models/telemetry-data';
import {ALERT_STATE, alertStateList, COLOR_LIST_EXTENDED} from '../../config/constants';
import {forkJoin} from 'rxjs';
import * as Highcharts from 'highcharts';
import _ from 'lodash';
import {BINARY, MULTISTATE} from '../../config/constants';
import {BackendService} from "../../services/backend/backend.service";
import {CoreSmartAlert, StateHistory} from "../../models/core/alert-rule.model";


@Injectable()
export class TelemetryDialogService {
    private dashStyle = [
        "solid",
        "shortdash",
        "longdash",
        "dashdot",
        "shortdot",
        "longdashdot",
        "shortdashdot",
        "shortdashdotdot",
        "dot",
        "dash",
        "longdashdotdot"
    ];

    constructor(private backendService: BackendService) {
    }

    public getTelemetryData(dataPoints: DataPoint[], start?: string, end?: string) {
        const startDate = start || moment.utc().subtract(30, 'days').format();
        const endDate = end || moment.utc().format();
        let requestsArray = dataPoints.map(dataPoint => {
            return this.backendService.getTelemetryData(dataPoint.id, startDate, endDate);
        });

        return forkJoin(requestsArray);
    }

    public formatTelemetryData(responseArray: TelemetryData[],
                               dataPoints: DataPoint[],
                               showPreview: boolean,
                               legendField: string = 'description', zoomValues?: Highcharts.ExtremesObject,
                               alertData?: CoreSmartAlert,
    ) {

        let chartConfig: ChartConfig = {data: [], yAxis: [], zoomValues: zoomValues};

        dataPoints.forEach((dataPoint, index) => {
            if (responseArray[index] == null) {
                return;
            }
            responseArray[index].objectName = dataPoint.objectName || 'N/A';
            responseArray[index].description = dataPoint.description || 'N/A';
            responseArray[index].customName = dataPoint.customName || 'N/A';
            responseArray[index].signalType = dataPoint.signalType;
        })
        let filterdByNull = responseArray.filter(telemetryData => telemetryData !== null);

        if (filterdByNull.length === 0) {
            return null;
        }

        const groupedByUnit = _.groupBy(filterdByNull, (telemetryData) => telemetryData.units);
        let colorIndex = 0;

        Object.keys(groupedByUnit).map((unit, index) => {
            let unitType = unit === "null" || unit === "noUnits" ? '' : unit;
            let color = '#000000';
            let legend = '';

            chartConfig.yAxis.push(
                {
                    title: {
                        text: unitType,
                        style: {
                            color: <string>color
                        }
                    },
                    labels: {
                        style: {
                            color: <string>color
                        }
                    },
                    opposite: false
                }
            )

            groupedByUnit[unit].forEach((telemetryData, i) => {
                if (telemetryData != null) {
                    legend = telemetryData[legendField];
                    if (unitType) {
                        legend = `${legend} [${unitType}]`;
                    }

                    chartConfig.data.push({
                        name: legend,
                        type: 'line',
                        dashStyle: this.getDashStyle(0),
                        yAxis: index,
                        step: (telemetryData.signalType === MULTISTATE || telemetryData.signalType === BINARY) ? 'left' : undefined,
                        color: this.getColorByIndex(colorIndex++),
                        data: telemetryData.samples.sort((a, b) => {
                            return a.timestamp - b.timestamp
                        }).map(entry => {
                            return {
                                x: entry.timestamp,
                                y: entry.value,
                                maskValue: entry.maskValue
                            };
                        }),
                        zIndex: 10,
                    });
                }
            })
        });


        this.addStatesPeriodToSeries(
            chartConfig,
            filterdByNull,
            Object.keys(groupedByUnit).length,
            alertData
        );

        return chartConfig;
    }

    private getDashStyle(index: number): string {
        return this.dashStyle[index];
    }

    private getColorByIndex(index: number): string {
        return COLOR_LIST_EXTENDED[index];
    }

    private addStatesPeriodToSeries(
        chartConfig: ChartConfig,
        telemetryData: TelemetryData[],
        yAxisIndex: number,
        alertData?: CoreSmartAlert
    ): void {
        if (!alertData) {
            return;
        }
        /* Handle for special case in PROD when alert rule is just created,
          stateHistory is empty, we have to handle to get active state */
        if (!alertData.stateHistory?.length) {
            let runningState: StateHistory = new Object() as StateHistory;
            runningState.startedAt = alertData.startDate as string;
            if (alertData.endDate) {
                runningState.endedAt = alertData.endDate as string;
            } else {
                runningState.endedAt = moment.utc(alertData.startDate)
                .add(alertData.durationInMinutes, 'minutes').toISOString();
            }
            runningState.state = ALERT_STATE.RUNNING;
            alertData.stateHistory.push(runningState);
        }
        let existedLegendName: string[] = [];
        alertData.stateHistory.forEach((item: StateHistory, index) => {
            let alertState = item;
            const maxSampleValue = this.getMaxSampleValue(telemetryData);
            const [minTs, maxTs] = this.getMinMaxTsValue(telemetryData);

            const alertStart = moment(alertState.startedAt).valueOf();
            let alertEnd = moment(alertState.endedAt).valueOf();
            if (alertEnd < minTs || alertStart > maxTs) {
                // If the alert is outside the telemetry data range, do not add it
                return;
            }

            const alertStateObj = alertStateList[alertState.state];
            let showInLegend = false;
            if (!existedLegendName.includes(alertStateObj.legend)) {
                showInLegend = true;
                existedLegendName.push(alertStateObj.legend);
            }
            chartConfig.yAxis.push({
                title: {
                    text: showInLegend ? alertStateObj.legend : '',
                    style: {
                        color: '#000000',
                    },
                },
                labels: {
                    style: {
                        color: '#FFF',
                    },
                },
                max: maxSampleValue,
                opposite: true,
            });

            chartConfig.data.push({
                events: {
                    legendItemClick: function () {
                        const chart = this.chart;
                        // Toggle all series with the same name
                        chart.series.forEach(s => {
                          if (s.name === this.name && s !== this) {
                            s.setVisible(!s.visible, false);
                          }
                        });
                        chart.redraw();
                        return false; // Prevent default behavior
                    }
                },
                showInLegend: showInLegend,
                name: alertStateObj.legend,
                type: 'area',
                yAxis: yAxisIndex,
                data: [
                    [Math.max(alertStart, minTs), maxSampleValue],
                    [Math.min(alertEnd, maxTs), maxSampleValue],
                ],
                color: this.getColorByIndex(yAxisIndex + alertStateObj.colorIndex),
                fillOpacity: 0.2,
                connectNulls: false,
                zIndex: 1,
            });
        })
    }

    private getMaxSampleValue(telemetryData: TelemetryData[]): number {
        const flattenedData = [].concat(
            ...telemetryData.map((data) => data.samples || [])
        );
        if (!flattenedData || flattenedData.length === 0) {
            return 1;
        }
        return Math.max(...flattenedData.map((sample) => sample.value), 1);
    }

    private getMinMaxTsValue(telemetryData: TelemetryData[]): number[] {
        const flattenedData = [].concat(
            ...telemetryData.map((data) => data.samples || [])
        );
        if (!flattenedData || flattenedData.length === 0) {
            return [0, 0];
        }
        let timestamps = flattenedData.map((sample) => sample.timestamp);
        const minTs = Math.min(...timestamps);
        const maxTs = Math.max(...timestamps);
        return [minTs, maxTs];
    }

}
